import { useEffect, useState } from "react";
import useCrmApi from "../../hook/crm-req";
import { toast } from "sonner";
import { Country, State, City } from "country-state-city";
import { CalendarIcon, ChevronUpIcon, XIcon } from "lucide-react";
import { ContactFormData, ContactModalProps } from "./types";
import useRequest from "../../hook/use-req";




const CustomerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.2166 17.3323C13.9349 15.9008 12.0727 15 10 15C7.92734 15 6.06492 15.9008 4.7832 17.3323M10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10C19 14.9706 14.9706 19 10 19ZM10 12C8.34315 12 7 10.6569 7 9C7 7.34315 8.34315 6 10 6C11.6569 6 13 7.34315 13 9C13 10.6569 11.6569 12 10 12Z" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TagBadge = ({ label, onRemove }: { label: string; onRemove: () => void }) => (
  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-400 text-xs font-medium">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="hover:text-primary-900 dark:hover:text-primary-300 transition-colors"
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      </svg>
    </button>
  </span>
);

const Select = ({ label, value, onChange, options, placeholder = "Select", disabled = false }: any) => (
  <div className="flex flex-col gap-1">
    {label && <label className="text-xs font-medium text-gray-600 dark:text-gray-400">{label}</label>}
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full appearance-none px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-50 dark:focus:ring-primary-500/20 transition-all text-gray-700 dark:text-gray-300 disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
      >
        <option value="">{placeholder}</option>
        {options.map((opt: any) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500">
        <ChevronUpIcon />
      </div>
    </div>
  </div>
);

const normalizeDialCode = (phoneCode?: string | number | null) => {
  if (!phoneCode) return "+234";
  const code = String(phoneCode);
  return code.startsWith("+") ? code : `+${code}`;
};

const countries = Country.getAllCountries().map((country) => ({
  value: country.name,
  label: country.name,
  isoCode: country.isoCode,
  phoneCode: normalizeDialCode(country.phonecode),
}));

const countryCodeOptions = Country.getAllCountries()
  .map((country) => ({
    value: normalizeDialCode(country.phonecode),
    label: `${normalizeDialCode(country.phonecode)} ${country.isoCode}`,
  }))
  .filter((country, index, list) => list.findIndex((item) => item.value === country.value) === index)
  .sort((a, b) => a.value.localeCompare(b.value));

const getCountryByName = (name?: string) =>
  countries.find((country) => country.value.toLowerCase() === (name || "").toLowerCase());

const getCountryByIso = (iso?: string | number | null) =>
  countries.find((country) => country.isoCode.toLowerCase() === String(iso || "").toLowerCase());

const getCountryByDialCode = (dialCode?: string | number | null) =>
  countries.find((country) => country.phoneCode === normalizeDialCode(dialCode));

const splitPhoneNumber = (phone: string, fallbackDialCode: string) => {
  const normalized = phone.replace(/\s/g, "");
  const matchedCountry = [...countries]
    .sort((a, b) => b.phoneCode.length - a.phoneCode.length)
    .find((country) => normalized.startsWith(country.phoneCode));

  const countryCode = matchedCountry?.phoneCode || fallbackDialCode;
  return {
    countryCode,
    localNumber: normalized.startsWith(countryCode)
      ? normalized.slice(countryCode.length)
      : normalized.replace(/^\+/, ""),
  };
};

const getStoredUserDefaults = () => {
  try {
    const stored = localStorage.getItem("userDetails");
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const resolveDefaultCountry = (defaultCountry?: string, defaultCountryCode?: string | number | null) =>
  getCountryByName(defaultCountry) ||
  getCountryByIso(defaultCountryCode) ||
  getCountryByDialCode(defaultCountryCode) ||
  getCountryByName("Nigeria") ||
  countries[0];

const resolveDefaultCountryCode = (defaultCountry?: string, defaultCountryCode?: string | number | null) => {
  if (String(defaultCountryCode || "").startsWith("+")) return normalizeDialCode(defaultCountryCode);
  return resolveDefaultCountry(defaultCountry, defaultCountryCode)?.phoneCode || "+234";
};

export function ContactModal({
  isOpen,
  onClose,
  conversationId,
  contact,
  defaultCountry,
  defaultCountryCode,
  fallbackName,
  onUpdated,
  onCreated,
  mode = "contact",
  variant = "modal",
}: ContactModalProps) {
  const isLead = mode === "lead";
  const isDrawer = variant === "drawer";
  const token = localStorage.getItem("authToken");
  const storedUserDefaults = getStoredUserDefaults();

  // `userDetails` in localStorage is only ever the /auth/login response
  // snapshot from whenever the user last signed in — it doesn't reliably
  // carry `country`/`country_code` (that's /auth/get_userdata's shape), so
  // this was silently falling through to hardcoded Nigeria for everyone.
  // Fetch the real signed-in user's own org country live as a fallback tier
  // between the stale cache and the hardcoded default.
  const [liveCountry, setLiveCountry] = useState<{ country?: string; countryCode?: number } | null>(null);
  const { makeRequest: getCurrentUser } = useRequest("/auth/get_userdata", "GET", { Authorization: `Bearer ${token}` });
  useEffect(() => {
    if (!isOpen || defaultCountry || defaultCountryCode) return;
    (async () => {
      const [response] = await getCurrentUser();
      const country = response?.payload?.country;
      const countryCode = response?.payload?.country_code;
      if (country || countryCode) setLiveCountry({ country, countryCode });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const modalDefaultCountry =
    defaultCountry ||
    storedUserDefaults?.country_name ||
    storedUserDefaults?.country ||
    liveCountry?.country ||
    "Nigeria";
  const modalDefaultCountryCode =
    defaultCountryCode ??
    storedUserDefaults?.country_code ??
    storedUserDefaults?.phone_code ??
    liveCountry?.countryCode ??
    "+234";
  const resolvedDefaultCountry = resolveDefaultCountry(modalDefaultCountry, modalDefaultCountryCode);
  const resolvedDefaultCountryCode = resolveDefaultCountryCode(modalDefaultCountry, modalDefaultCountryCode);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = !!contact?.id;
  const resourceBase = isLead ? "/leads" : "/contacts";

  const [source, setSource] = useState("website");
  const [score, setScore] = useState("");

  const { makeRequest: createContactRequest } = useCrmApi(resourceBase, "POST", {
    Authorization: `Bearer ${token}`,
  });
  const { makeRequest: updateContactRequest } = useCrmApi(`${resourceBase}/${contact?.id || ""}`, "PUT", {
    Authorization: `Bearer ${token}`,
  });

  const [form, setForm] = useState<ContactFormData>({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    country: resolvedDefaultCountry?.value || "Nigeria",
    state: "",
    city: "",
    age: "",
    gender: "",
    dob: "",
    incomeOperator: "Is More than",
    incomeAmount: "",
    tags: [],
    tagInput: "",
    countryCode: resolvedDefaultCountryCode,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableStates, setAvailableStates] = useState<Array<{ value: string; label: string }>>([]);
  const [availableCities, setAvailableCities] = useState<Array<{ value: string; label: string }>>([]);

  // Nigeria States


  useEffect(() => {
    if (!isOpen) return;

    let existingTags: string[] = [];
    if (contact?.customer_tags) {
      if (typeof contact.customer_tags === "string") {
        existingTags = contact.customer_tags.split(",").map((t: string) => t.trim());
      } else if (Array.isArray(contact.customer_tags)) {
        existingTags = contact.customer_tags;
      }
    } else if (Array.isArray(contact?.tags)) {
      existingTags = contact.tags;
    }

    setSource(contact?.source || "website");
    setScore(contact?.score != null ? String(contact.score) : "");

    const phone = contact?.phone || "";
    const { countryCode, localNumber } = phone
      ? splitPhoneNumber(phone, resolvedDefaultCountryCode)
      : { countryCode: resolvedDefaultCountryCode, localNumber: "" };
    const countryName = contact?.custom?.country || resolvedDefaultCountry?.value || "Nigeria";

    setForm({
      firstName: contact?.first_name || contact?.firstName || fallbackName || "",
      lastName: contact?.last_name || contact?.lastName || "",
      phone: localNumber,
      email: contact?.email || "",
      country: String(countryName),
      state: String(contact?.custom?.state || ""),
      city: String(contact?.custom?.city || ""),
      age: String(contact?.custom?.age || ""),
      gender: String(contact?.custom?.gender || ""),
      dob: String(contact?.custom?.dob || contact?.dob || ""),
      incomeOperator: "Is More than",
      incomeAmount: String(contact?.custom?.income || "").replace(/^(Is More than|Is Less than|Is Equal to)\s*/i, ""),
      tags: existingTags,
      tagInput: "",
      countryCode,
    });

    setErrors({});
  }, [contact, isOpen, resolvedDefaultCountry?.value, resolvedDefaultCountryCode, fallbackName]);

  // Update states when country changes
  useEffect(() => {
    if (!form.country) {
      setAvailableStates([]);
      setAvailableCities([]);
      return;
    }

    const selectedCountry = getCountryByName(form.country);
    const statesList = selectedCountry
      ? State.getStatesOfCountry(selectedCountry.isoCode).map((state: any) => ({
        value: state.name,
        label: state.name,
      }))
      : [];

    setAvailableStates(statesList);
    setForm(prev => ({
      ...prev,
      state: statesList.some((state) => state.value === prev.state) ? prev.state : "",
      city: "",
    }));
    setAvailableCities([]);
  }, [form.country]);

  // Update cities when state changes
  useEffect(() => {
    if (!form.state || !form.country) {
      setAvailableCities([]);
      return;
    }

    const selectedCountry = getCountryByName(form.country);
    const stateObj = selectedCountry
      ? State.getStatesOfCountry(selectedCountry.isoCode).find((state: any) => state.name === form.state)
      : null;

    let citiesList: Array<{ value: string; label: string }> = [];
    if (selectedCountry && stateObj) {
      citiesList = City.getCitiesOfState(selectedCountry.isoCode, stateObj.isoCode).map((city: any) => ({
        value: city.name,
        label: city.name,
      }));
    }

    setAvailableCities(citiesList);
    setForm(prev => ({
      ...prev,
      city: citiesList.some((city) => city.value === prev.city) ? prev.city : "",
    }));
  }, [form.state, form.country]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.firstName.trim()) newErrors.firstName = "First name is required";
    if (!form.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!isLead && !form.phone.trim()) newErrors.phone = "Phone number is required";
    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(form.email)) {
      newErrors.email = "Invalid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && form.tagInput.trim()) {
      e.preventDefault();
      if (!form.tags.includes(form.tagInput.trim())) {
        setForm(prev => ({
          ...prev,
          tags: [...prev.tags, prev.tagInput.trim()],
          tagInput: ""
        }));
      } else {
        setForm(prev => ({ ...prev, tagInput: "" }));
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const onSubmitContact = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const incomeValue = form.incomeAmount ? `${form.incomeOperator} ${form.incomeAmount}` : "";
      const custom = {
        ...(contact?.custom || {}),
        country: form.country,
        state: form.state,
        city: form.city,
        status: contact?.custom?.status || contact?.status || "unknown",
        age: form.age,
        gender: form.gender,
        dob: form.dob,
        income: incomeValue,
      };

      const trimmedPhone = form.phone.replace(/\D/g, "");
      const payload: Record<string, unknown> = {
        first_name: form.firstName,
        last_name: form.lastName,
        ...(trimmedPhone && { phone: `${form.countryCode}${trimmedPhone}` }),
        email: form.email,
        tags: form.tags,
        custom,
        ...(conversationId && { conversation_id: conversationId }),
      };

      // source/score are lead-only fields, and only appear on the real
      // POST /leads payload — the PUT /leads/{id} payload doesn't carry them.
      if (isLead && !isEditMode) {
        payload.source = source || "website";
        payload.score = score ? Number(score) : 0;
      }

      const resourceLabel = isLead ? "lead" : "contact";
      let response, status;

      if (isEditMode) {
        [response, status] = await updateContactRequest(payload);
        if (status >= 200 && status < 300) {
          const updatedContact = response?.data || response;
          toast.success(response?.message || `${isLead ? "Lead" : "Contact"} updated successfully!`);
          onUpdated?.(updatedContact);
          onClose();
        } else {
          toast.error(response?.detail || response?.message || `Failed to update ${resourceLabel}`);
        }
      } else {
        [response, status] = await createContactRequest(payload);
        if (status >= 200 && status < 300) {
          const newContact = response?.data || response;
          toast.success(response?.message || `${isLead ? "Lead" : "Contact"} created successfully!`);
          onCreated?.(newContact);
          onClose();
        } else {
          toast.error(response?.detail || response?.message || `Failed to create ${resourceLabel}`);
        }
      }
    } catch (error) {
      toast.error(isEditMode ? `An error occurred while updating the ${isLead ? "lead" : "contact"}` : `An error occurred while creating the ${isLead ? "lead" : "contact"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const formContent = (
    <div className={isDrawer ? "h-screen flex flex-col" : "w-[50%] mx-auto"}>
      {/* Header */}
      <div className="flex items-start justify-between px-7 pt-6 pb-5 border-b border-gray-100 dark:border-gray-800 shrink-0">
        <div>
          {/* <h2 className="text-[20px] font-semibold text-[#3A3428] leading-snug">
            {isLead
              ? (isEditMode ? "Update Lead" : "Add New Lead")
              : (isEditMode ? "Update Customer" : "Create  Customer")}
          </h2> */}
          <p className="text-md text-[#434343] dark:text-gray-400 mt-0.5">
            {isLead
              ? (isEditMode ? "Update lead details" : "Add a new lead")
              : (isEditMode ? "Update customer details" : "Add a new customer")}
          </p>
        </div>
        {!isDrawer && (
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors mt-0.5"
          >
            <XIcon />
          </button>
        )}
      </div>

      {/* Scrollable Body */}
      <div className="overflow-y-auto px-7 py-6 flex-1">
            <form onSubmit={(e) => { e.preventDefault(); onSubmitContact(); }} id="contact-form">
              <div className="flex items-center gap-2 mb-5">
                <CustomerIcon />
                <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">{isLead ? "Lead Details" : "Customer Details"}</span>
              </div>

              {/* Name */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">First Name</label>
                  <input
                    type="text"
                    placeholder="First Name"
                    value={form.firstName}
                    onChange={(e) => setForm(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-50 dark:focus:ring-primary-500/20 transition-all text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                  {errors.firstName && <p className="text-xs text-red-500">{errors.firstName}</p>}
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Last Name</label>
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={form.lastName}
                    onChange={(e) => setForm(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-50 dark:focus:ring-primary-500/20 transition-all text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                  {errors.lastName && <p className="text-xs text-red-500">{errors.lastName}</p>}
                </div>
              </div>

              {/* Phone & Email */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    Phone / WhatsApp{isLead && <span className="text-[#7B7B7B] font-semibold dark:text-gray-500"> (optional)</span>}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative w-24 shrink-0">
                      <select
                        value={form.countryCode}
                        onChange={(e) => setForm(prev => ({ ...prev, countryCode: e.target.value }))}
                        className="w-full appearance-none pl-3 pr-7 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-gray-700 dark:text-gray-300"
                      >
                        {countryCodeOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500">
                        <ChevronUpIcon />
                      </div>
                    </div>
                    <div className="flex-1">
                      <input
                        type="tel"
                        placeholder="Phone number"
                        value={form.phone}
                        onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-50 dark:focus:ring-primary-500/20 transition-all text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                  </div>
                  {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Email</label>
                  <input
                    type="email"
                    placeholder="Email"
                    value={form.email}
                    onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-50 dark:focus:ring-primary-500/20 transition-all text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                  {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                </div>
              </div>

              {/* Lead-only: source & score — only on create, matching the real POST /leads payload */}
              {isLead && !isEditMode && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Source</label>
                    <input
                      type="text"
                      placeholder="e.g. website"
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-50 dark:focus:ring-primary-500/20 transition-all text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Score</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      placeholder="0"
                      value={score}
                      onChange={(e) => setScore(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-50 dark:focus:ring-primary-500/20 transition-all text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                    />
                  </div>
                </div>
              )}

              {/* Country, State, City */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <Select
                  label="Country"
                  value={form.country}
                  onChange={(val: string) => {
                    const selectedCountry = getCountryByName(val);
                    setForm(prev => ({
                      ...prev,
                      country: val,
                      countryCode: selectedCountry?.phoneCode || prev.countryCode,
                    }));
                  }}
                  options={countries}
                  placeholder="Select Country"
                />

                <Select
                  label="State"
                  value={form.state}
                  onChange={(val: string) => setForm(prev => ({ ...prev, state: val }))}
                  options={availableStates}
                  placeholder="Select State"
                  disabled={!form.country}
                />

                <Select
                  label="City"
                  value={form.city}
                  onChange={(val: string) => setForm(prev => ({ ...prev, city: val }))}
                  options={availableCities}
                  placeholder="Select City"
                  disabled={!form.state}
                />
              </div>

              {/* Age, Gender, DOB */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <Select
                  label="Age"
                  value={form.age}
                  onChange={(val: string) => setForm(prev => ({ ...prev, age: val }))}
                  placeholder="Select Age Group"
                  options={[
                    { value: "Youth (18-24)", label: "Gen Z (18-24)" },
                    { value: "Early Career (25-34)", label: "Young Professionals (25-34)" },
                    { value: "Mid-Career (25-34) (25-34)", label: "Mid-Career (35-44)" },
                    { value: " Established Adults", label: " Established Adults (45–54)" },
                    { value: "Mature Audience", label: "Mature Audience (55-64)" },
                    { value: "Seniors", label: "Seniors (65+)" },
                  ]}
                />

                <Select
                  label="Gender"
                  value={form.gender}
                  onChange={(val: string) => setForm(prev => ({ ...prev, gender: val }))}
                  placeholder="Select Gender"
                  options={[
                    { value: "Male", label: "Male" },
                    { value: "Female", label: "Female" },
                    { value: "Other", label: "Other" },
                  ]}
                />

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400">DOB</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="DD/MM/YY"
                      value={form.dob}
                      onChange={(e) => setForm(prev => ({ ...prev, dob: e.target.value }))}
                      className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-50 dark:focus:ring-primary-500/20 transition-all text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none">
                      <CalendarIcon />
                    </div>
                  </div>
                </div>
              </div>

              {/* Income — leads have no income concept, contacts still do */}
              {!isLead && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Income</label>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Select
                        value={form.incomeOperator}
                        onChange={(val: string) => setForm(prev => ({ ...prev, incomeOperator: val }))}
                        options={[
                          { value: "Is More than", label: "Is More than" },
                          { value: "Is Less than", label: "Is Less than" },
                          { value: "Is Exactly", label: "Is Exactly" },
                          { value: "Is Between", label: "Is Between" },
                        ]}
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        placeholder="Amount"
                        value={form.incomeAmount}
                        onChange={(e) => setForm(prev => ({ ...prev, incomeAmount: e.target.value }))}
                        className="w-full px-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-50 dark:focus:ring-primary-500/20 transition-all text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Tags */}
              <div className="mb-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Customer Tags</label>
                <div className="flex flex-wrap gap-1.5 items-center px-3 py-2 min-h-[42px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-50 dark:focus-within:ring-primary-500/20 transition-all">
                  {form.tags.map((tag) => (
                    <TagBadge key={tag} label={tag} onRemove={() => removeTag(tag)} />
                  ))}
                  <input
                    value={form.tagInput}
                    onChange={(e) => setForm(prev => ({ ...prev, tagInput: e.target.value }))}
                    onKeyDown={addTag}
                    placeholder={form.tags.length === 0 ? "Add tags..." : ""}
                    className="flex-1 min-w-[80px] text-sm outline-none border-none bg-transparent text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
                <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">Press Enter to add a tag</p>
              </div>
            </form>
          </div>

      {/* Footer */}
      <div className="px-7 pb-6 pt-4 border-t border-gray-100 dark:border-gray-800 flex flex-col gap-2.5 shrink-0">
        <button
          type="submit"
          form="contact-form"
          disabled={isSubmitting}
          className="w-full py-3 rounded-xl bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
        >
          {isSubmitting
            ? "Saving..."
            : isLead
            ? (isEditMode ? "Update Lead" : "Add Lead")
            : (isEditMode ? "Update Contact" : "Create Contact")}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-full py-3 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  if (isDrawer) return formContent;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(2px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#FAFAFA] dark:bg-gray-900 rounded-2xl shadow-xl w-full overflow-auto flex flex-col" style={{ maxHeight: "90vh" }}>
        {formContent}
      </div>
    </div>
  );
}
